import _ from 'lodash'
import { apolloClient } from '@/graphql/apollo'
import { GET_ALL_BOOKMARKS_QUERY, GET_ALL_BOOKMARKS_WITH_ALL_TAGS_QUERY, GET_BOOKMARK_QUERY, GET_BOOKMARK_BY_URL_QUERY, PARSE_URL_QUERY, CREATE_BOOKMARK_MUTATION, UPDATE_BOOKMARK_MUTATION, DELETE_BOOKMARK_MUTATION, REPARSE_BOOKMARK_MUTATION, WAYBACK_BOOKMARK_MUTATION } from '@/graphql/queries/bookmarks'
import { normalize } from 'normalizr'
import { bookmarkSchema, bookmarksSchema, tagsSchema } from '@/schema'

import { initStateMixin, gettersMixin, actionsMixin, mutationsMixin } from '@/mixins/crud/store'

const initState = () => {
  return initStateMixin()
}

// initial state
const state = initState()

// getters
const getters = {
  ...gettersMixin,
  byUrl: (state) => (url) => {
    return _.find(state.entities, ['url', url]);
  }
}

// actions
const actions = {
  ...actionsMixin,
  // loadAll: async ({ commit }, {force = false}) => {
  loadAll: async ({ commit }, { force, page, filter }) => {
    force = force || false
    page = parseInt(page) || 1
    commit('setLoading', true, { root: true })

    const fetchPolicy = force ? 'network-only' : 'cache-first'
    const response = await apolloClient.query({ query: GET_ALL_BOOKMARKS_QUERY, variables: { page: page, filter: filter, order: 'updatedAt', direction: 'desc' }, fetchPolicy: fetchPolicy })

    let normalizedData = normalize(response.data, bookmarksSchema)
    commit('updateEntities', { normalizedData }, { root: true })
    commit('setLoading', false, { root: true })
  },
  loadPageWithAllTags: async ({ commit }, { force, page, filter }) => {
    force = force || false
    page = parseInt(page) || 1
    commit('setLoading', true, { root: true })

    const fetchPolicy = force ? 'network-only' : 'cache-first'
    const response = await apolloClient.query({ query: GET_ALL_BOOKMARKS_WITH_ALL_TAGS_QUERY, variables: { page: page, filter: filter, order: 'updatedAt', direction: 'desc' }, fetchPolicy: fetchPolicy })

    let normalizedBookmarks = normalize({bookmarks: response.data.bookmarks}, bookmarksSchema)
    let normalizedTags = normalize({tags: response.data.tags}, tagsSchema)
    const normalizedData = _.merge(normalizedBookmarks, normalizedTags)
    commit('updateEntities', { normalizedData }, { root: true })
    commit('setLoading', false, { root: true })
  },
  appendPage: async ({ commit }, { force, page, filter }) => {
    force = force || false
    page = parseInt(page) || 1
    commit('setLoading', true, { root: true })

    const fetchPolicy = force ? 'network-only' : 'cache-first'
    const response = await apolloClient.query({ query: GET_ALL_BOOKMARKS_QUERY, variables: { page: page, filter: filter, order: 'updatedAt', direction: 'desc' }, fetchPolicy: fetchPolicy })

    const normalizedData = normalize({bookmarks: response.data.bookmarks}, bookmarksSchema)
    const appendResults = true
    commit('updateEntities', { normalizedData, appendResults }, { root: true })
    commit('setLoading', false, { root: true })

    return response
  },
  loadById: async ({ commit }, id) => {
    let response = null

    const parsedId = parseInt(id)
    if (typeof(parsedId) == 'number' && parsedId > 0) {
      commit('setLoading', true, { root: true })

      response = await apolloClient.query({ query: GET_BOOKMARK_QUERY, variables: { id: parsedId } })
      const normalizedData = normalize(response.data, bookmarkSchema)
      commit('updateEntities', { normalizedData, appendResults: true }, { root: true })

      commit('setLoading', false, { root: true })
    }

    return response
  },
  loadByUrl: async ({ commit }, {url, parse}) => {
    let response = null
    parse = parse || 'false'

    commit('setLoading', true, { root: true })

    response = await apolloClient.query({ query: GET_BOOKMARK_BY_URL_QUERY, variables: { url: url, parse: parse } })
    //rename key
    Object.assign(response.data, {bookmark: response.data.bookmarkByUrl });

    const normalizedData = normalize(response.data, bookmarkSchema)
    commit('updateEntities', { normalizedData }, { root: true })
    commit('setLoading', false, { root: true })

    return response
  },
  parseUrl: async ({ commit }, url) => {
    let response = null

    commit('setLoading', true, { root: true })

    response = await apolloClient.query({ query: PARSE_URL_QUERY, variables: { url: url } })
    const normalizedData = normalize(response.data, bookmarkSchema)
    commit('updateEntities', { normalizedData }, { root: true })

    commit('setLoading', false, { root: true })

    return response
  },
  save: async ({ commit }, { id, form }) => {
    let response = null

    commit('setLoading', true, { root: true })
    if (id) {
      response = await apolloClient.mutate({ mutation: UPDATE_BOOKMARK_MUTATION, variables: {id: id, bookmark: form} })
    } else {
      response = await apolloClient.mutate({ mutation: CREATE_BOOKMARK_MUTATION, variables: {bookmark: form} })
    }

    if (response) {
      if (!id) {
        commit('increaseTotalCount')
      }

      apolloClient.clearStore()

      Object.assign(response.data, {bookmark: response.data.bookmarks });
      const normalizedData = normalize(response.data, bookmarkSchema)
      commit('updateEntities', { normalizedData, appendResults: true }, { root: true })
    }

    commit('setLoading', false, { root: true })

    return response
  },

  delete: async ({ commit }, ids) => {
    commit('setLoading', true, { root: true })

    const response = await apolloClient.mutate({ mutation: DELETE_BOOKMARK_MUTATION, variables: { ids: ids } })
    response.data.bookmarks.map(m => {
      commit('removeEntity', m.id)
    })
    apolloClient.clearStore()

    commit('setLoading', false, { root: true })

    return response
  },

  reparse: async ({ commit }, id) => {
    commit('setLoading', true, { root: true })

    const response = await apolloClient.mutate({ mutation: REPARSE_BOOKMARK_MUTATION, variables: { id: id } })
    const normalizedData = normalize(response.data, bookmarkSchema)
    commit('updateEntities', { normalizedData, appendResults: true }, { root: true })

    commit('setLoading', false, { root: true })

    return response
  },

  wayback: async ({ commit }, id) => {
    commit('setLoading', true, { root: true })

    const response = await apolloClient.mutate({ mutation: WAYBACK_BOOKMARK_MUTATION, variables: { id: id } })

    commit('setLoading', false, { root: true })

    return response.data.wayback
  }
}

// mutations
const mutations = {
  ...mutationsMixin
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
